import type { SeriesOption } from '@/modules/trade-rule-builder/model/trade-rule-builder';
import {
  TradeRule,
  ConditionGroup,
  Condition,
  isGroup,
} from '@/modules/trade-rule-builder/model/trade-rule-builder';

export interface ValidationValues {
  [seriesId: string]: { value: string; valid: boolean };
}

interface Props {
  rule: TradeRule;
  validationValues: ValidationValues;
  seriesOptions?: SeriesOption[];
}

// Helper to parse value as number
const getValue = (val: string) => {
  const num = parseFloat(val);
  return isNaN(num) ? undefined : num;
};

// Evaluate a single condition
function evaluateConditionValue(
  value: number | undefined,
  cmp: string,
  target: number,
  condId?: string,
): boolean {
  if (value === undefined) {
    return false;
  }
  let result = false;
  switch (cmp) {
    case '>':
      result = value > target;
      break;
    case '<':
      result = value < target;
      break;
    case '>=':
      result = value >= target;
      break;
    case '<=':
      result = value <= target;
      break;
    case '=':
      result = value === target;
      break;
    case '!=':
      result = value !== target;
      break;
    default:
      result = false;
  }
  return result;
}

// Recursively evaluate a group
function evaluateGroup(
  group: ConditionGroup,
  values: ValidationValues,
  debug: boolean,
  resultMap: Record<string, boolean>,
): boolean {
  let groupResult: boolean;
  if (group.operator === 'AND') {
    let allTrue = true;
    for (const child of group.children) {
      let res;
      if (isGroup(child)) {
        res = evaluateGroup(child, values, debug, resultMap);
        resultMap[child.id] = res;
      } else {
        const v = getValue(values[child.seriesId]?.value ?? '');
        res = evaluateConditionValue(v, child.comparator, child.value, child.id);
        resultMap[child.id] = res;
      }
      if (!res) allTrue = false;
    }
    groupResult = allTrue;
    return groupResult;
  } else if (group.operator === 'OR') {
    let anyTrue = false;
    for (const child of group.children) {
      let res;
      if (isGroup(child)) {
        res = evaluateGroup(child, values, debug, resultMap);
        resultMap[child.id] = res;
      } else {
        const v = getValue(values[child.seriesId]?.value ?? '');
        res = evaluateConditionValue(v, child.comparator, child.value, child.id);
        resultMap[child.id] = res;
      }
      if (res) anyTrue = true;
    }
    groupResult = anyTrue;
    return groupResult;
  }
  return false;
}

export default function TradeRuleValidation({ rule, validationValues, seriesOptions }: Props) {
  const resultMap: Record<string, boolean> = {};
  const allValuesEntered = Object.values(validationValues).every(v => v.value !== '');
  // Always evaluate to fill resultMap
  const groupResult = evaluateGroup(rule.rootGroup, validationValues, false, resultMap);
  // Only show TRUE for rule if all values are entered and group passes
  const result = allValuesEntered && groupResult;
  resultMap[rule.rootGroup.id] = groupResult;

  // Helper to get display name for a seriesId
  const getDisplayName = (seriesId: string | number) => {
    const sid = String(seriesId);
    const found = seriesOptions?.find(opt => String(opt.dataSeriesId) === sid);
    return found ? found.display : sid;
  };

  // Helper to render group/condition status
  const renderGroup = (group: ConditionGroup) => {
    // Find pairs for formatting
    const conditions = group.children.filter(c => !isGroup(c)) as Condition[];
    const formattedPairs: Set<string> = new Set();
    const pairElements: JSX.Element[] = [];
    for (let i = 0; i < conditions.length; i++) {
      const c1 = conditions[i];
      for (let j = i + 1; j < conditions.length; j++) {
        const c2 = conditions[j];
        if (
          c1.seriesId === c2.seriesId &&
          ((['<', '<='].includes(c1.comparator) && ['>', '>='].includes(c2.comparator)) ||
            (['<', '<='].includes(c2.comparator) && ['>', '>='].includes(c1.comparator)))
        ) {
          // Use mathematical convention: lower <[=] x <[=] upper
          let lower, upper;
          if (['>', '>='].includes(c1.comparator)) {
            lower = c1;
            upper = c2;
          } else {
            lower = c2;
            upper = c1;
          }
          const leftSymbol = lower.comparator === '>=' ? '≤' : '<';
          const rightSymbol = upper.comparator === '<=' ? '≤' : '<';
          formattedPairs.add(c1.id);
          formattedPairs.add(c2.id);
          const bothTrue = resultMap[c1.id] === true && resultMap[c2.id] === true;
          pairElements.push(
            <div key={c1.id + '-' + c2.id}>
              Range: {lower.value} {leftSymbol} <b>{getDisplayName(lower.seriesId)}</b>{' '}
              {rightSymbol} {upper.value}{' '}
              {bothTrue ? (
                <span style={{ color: 'green' }}>✓</span>
              ) : (
                <span style={{ color: 'red' }}>✗</span>
              )}
            </div>,
          );
        }
      }
    }
    return (
      <div style={{ marginLeft: 16, borderLeft: '2px solid #eee', paddingLeft: 8 }}>
        <div>
          <b>Group ({group.operator})</b>
          {resultMap[group.id] === true ? (
            <span style={{ color: 'green' }}>✓</span>
          ) : (
            <span style={{ color: 'red' }}>✗</span>
          )}
        </div>
        {/* Render formatted pairs first */}
        {pairElements}
        {/* Render other conditions and subgroups */}
        {group.children.map(child =>
          isGroup(child) ? (
            <div key={child.id}>{renderGroup(child)}</div>
          ) : (
            !formattedPairs.has(child.id) && (
              <div key={child.id}>
                Condition: <b>{getDisplayName(String(child.seriesId))}</b> {child.comparator}{' '}
                {child.value} &rarr;{' '}
                {typeof validationValues[child.seriesId]?.value === 'string' &&
                validationValues[child.seriesId]?.value !== '' ? (
                  resultMap[child.id] === true ? (
                    <span style={{ color: 'green' }}>✓</span>
                  ) : (
                    <span style={{ color: 'red' }}>✗</span>
                  )
                ) : (
                  <span style={{ color: '#888' }}>–</span>
                )}
              </div>
            )
          ),
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="mb-2">
        <b>Rule Result:</b>{' '}
        <span style={{ color: result ? 'green' : 'red', fontWeight: 'bold' }}>
          {result ? 'TRUE' : 'FALSE'}
        </span>
        {result && (
          <span
            style={{ color: '#706e6eff', fontWeight: 'normal', marginLeft: 4, fontSize: '0.8em' }}
          >
            (Actions Will Execute)
          </span>
        )}
      </div>
      {renderGroup(rule.rootGroup)}
    </div>
  );
}
